import React, { useState, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import AddPipeFittingModal from "./AddPipeFittingModal";
import { apiClient } from "../../../services/api/apiClient";
import SearchableSelect from "../../ui/SearchableSelect";

function SpecificationFormModal({
  isOpen,
  onClose,
  onSubmit,
  editData,
  pvfSubmittalId,
  onNewSpecification,
  isContinuousAddingMode,
}) {
  const initialSizes = [
    {
      start: "All",
      end: "All",
      isToggleOn: false,
      pipeOptions: [],
      fittingOptions: [],
      material: "",
      pipe: "",
      fitting: "",
      shutoff1: "",
      shutoff2: "",
      throttle: "",
      check: "",
      strainer: "",
    },
  ];

  const [sizes, setSizes] = useState(initialSizes);
  const [activeButtonIndex, setActiveButtonIndex] = useState(0);
  const [systems, setSystems] = useState([""]);
  const [allSystems, setAllSystems] = useState([]);
  const [systemInputTypes, setSystemInputTypes] = useState(["dropdown"]);
  const [newSystemNames, setNewSystemNames] = useState([""]);
  const [materials, setMaterials] = useState([]);
  const [productTypes, setProductTypes] = useState({});
  const [valveProducts, setValveProducts] = useState({
    shutoffValve: [],
    throttleValve: [],
    checkValve: [],
    strainer: [],
  });
  const [allSizes, setAllSizes] = useState([]);
  const [specificationName, setSpecificationName] = useState("");
  const [deletedSizes, setDeletedSizes] = useState([]);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);

  // Effect to reset form when opening modal for new specification
  useEffect(() => {
    if (isOpen && !editData) {
      // Reset form to initial values for new specification
      setSpecificationName("");
      setSystems([""]);
      setSystemInputTypes(["dropdown"]);
      setNewSystemNames([""]);
      setSizes(initialSizes);
      setActiveButtonIndex(0);
      setDeletedSizes([]);
    }
  }, [isOpen, editData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          systemsResponse,
          materialsResponse,
          productTypesResponse,
          sizesResponse,
          productsResponse,
        ] = await Promise.all([
          apiClient.get("/systems"),
          apiClient.get("/materials"),
          apiClient.get("/product_types"),
          apiClient.get("/sizes"),
          apiClient.get("/products"),
        ]);

        // this is the old way of extracting the response
        // const systems = systemsResponse;
        // const materials = materialsResponse;
        // const productTypes = productTypesResponse;
        // const sizes = sizesResponse;
        // const products = productsResponse;
        // Extract data from the new standardized response format
        const systems = systemsResponse.data;
        const materials = materialsResponse.data;
        const productTypes = productTypesResponse.data;
        const sizes = sizesResponse.data;
        const products = productsResponse.data;

        setAllSystems(systems);
        setMaterials(materials);
        setActiveButtonIndex(0);

        // Process product types to create the types object
        const types = {};
        productTypes.forEach((type) => {
          types[type.name] = type.id;
        });
        setProductTypes(types);

        setAllSizes(sizes);

        // Fetch valve products after product types are loaded
        if (Object.keys(types).length > 0) {
          const shutoffValves = products.filter(
            (p) => p.product_type_id === types["shutoff_valve"]
          );
          const throttleValves = products.filter(
            (p) => p.product_type_id === types["throttle"]
          );
          const valveProductsData = {
            shutoffValve: shutoffValves,
            throttleValve: [...shutoffValves, ...throttleValves], // Combine here
            checkValve: products.filter(
              (p) => p.product_type_id === types["check_valve"]
            ),
            strainer: products.filter(
              (p) => p.product_type_id === types["strainer"]
            ),
          };
          setValveProducts(valveProductsData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const fetchProductsByMaterial = useCallback(
    async (materialId) => {
      try {
        const response = await apiClient.get(
          `/products?material_id=${materialId}`
        );
        // const data = response;
        // Extract data from the new standardized response format this will be the new format when we change the materials too
        const data = response.data;
        const pipeOptions = data.filter(
          (p) => p.product_type_id === productTypes["pipe"]
        );
        const fittingOptions = data.filter(
          (p) => p.product_type_id === productTypes["fittings"]
        );
        return { pipeOptions, fittingOptions };
      } catch (error) {
        console.error("Error fetching products by material:", error);
        return { pipeOptions: [], fittingOptions: [] };
      }
    },
    [productTypes]
  );

  useEffect(() => {
    const populateFields = async () => {
      if (editData) {
        setActiveButtonIndex(0);
        setSpecificationName(editData.name);

        // Populate systems with previous values
        const previousSystemIds = editData.system_ids || [];
        setSystems(previousSystemIds);

        // When editing, all systems are existing (dropdown type)
        setSystemInputTypes(previousSystemIds.map(() => "dropdown"));
        setNewSystemNames(previousSystemIds.map(() => ""));

        const sortedSizes = [...editData.sizes].sort((a, b) => {
          const sizeOrder = allSizes.findIndex(
            (size) => size.size === a.min_size_name
          );
          const nextSizeOrder = allSizes.findIndex(
            (size) => size.size === b.min_size_name
          );
          return sizeOrder - nextSizeOrder;
        });

        const updatedSizes = await Promise.all(
          sortedSizes.map(async (size) => {
            const selectedMaterial = materials.find(
              (material) => material.name === size.material_name
            );

            // Fetch products (pipe and fitting options) based on selected material
            const { pipeOptions, fittingOptions } = selectedMaterial
              ? await fetchProductsByMaterial(selectedMaterial.id)
              : { pipeOptions: [], fittingOptions: [] };

            return {
              ...size,
              start: size.min_size_name,
              end: size.max_size_name,
              isToggleOn: !size.valves,
              material: selectedMaterial ? selectedMaterial.id : "",
              pipe:
                pipeOptions.find((pipe) => pipe.name === size.pipe_name)?.id ||
                "",
              fitting:
                fittingOptions.find(
                  (fitting) => fitting.name === size.fitting_name
                )?.id || "",
              pipeOptions:
                pipeOptions.length > 0
                  ? pipeOptions
                  : [{ id: size.pipe_id, name: size.pipe_name }],
              fittingOptions:
                fittingOptions.length > 0
                  ? fittingOptions
                  : [{ id: size.fitting_id, name: size.fitting_name }],
              shutoff1:
                valveProducts.shutoffValve.find(
                  (valve) => valve.name === size.shutoff1_name
                )?.id || "",
              shutoff2:
                valveProducts.shutoffValve.find(
                  (valve) => valve.name === size.shutoff2_name
                )?.id || "",
              throttle:
                valveProducts.throttleValve.find(
                  (valve) => valve.name === size.throttle_name
                )?.id || "",
              check:
                valveProducts.checkValve.find(
                  (valve) => valve.name === size.check_name
                )?.id || "",
              strainer:
                valveProducts.strainer.find(
                  (strainer) => strainer.name === size.strainer_name
                )?.id || "",
            };
          })
        );

        setSizes(updatedSizes);
      } else {
        // Reset the form to initial values when editData is null
        setSpecificationName("");
        setSystems([""]);
        setSizes([
          {
            start: "All",
            end: "All",
            isToggleOn: false,
            pipeOptions: [],
            fittingOptions: [],
            material: "",
            pipe: "",
            fitting: "",
            shutoff1: "",
            shutoff2: "",
            throttle: "",
            check: "",
            strainer: "",
          },
        ]);
      }
    };

    if (
      materials.length > 0 &&
      Object.keys(valveProducts).length > 0 &&
      allSystems.length > 0
    ) {
      populateFields();
    }
  }, [
    editData,
    materials,
    valveProducts,
    fetchProductsByMaterial,
    allSizes,
    allSystems,
  ]);

  const handleMaterialChange = async (event, index) => {
    const materialId = event.target.value;
    const { pipeOptions, fittingOptions } = await fetchProductsByMaterial(
      materialId
    );
    const newSizes = [...sizes];
    newSizes[index] = {
      ...newSizes[index],
      material: materialId || "",
      pipeOptions,
      fittingOptions,
    };
    setSizes(newSizes);
  };

  const handlePipeChange = (event, index) => {
    const pipeId = event.target.value;
    const newSizes = [...sizes];
    newSizes[index] = { ...newSizes[index], pipe: pipeId || "" };
    setSizes(newSizes);
  };

  const handleFittingChange = (event, index) => {
    const fittingId = event.target.value;
    const newSizes = [...sizes];
    newSizes[index] = { ...newSizes[index], fitting: fittingId || "" };
    setSizes(newSizes);
  };

  const handleShutoff1Change = (event, index) => {
    const shutoff1Id = event.target.value;
    const newSizes = [...sizes];
    newSizes[index] = { ...newSizes[index], shutoff1: shutoff1Id || "" };
    setSizes(newSizes);
  };

  const handleShutoff2Change = (event, index) => {
    const shutoff2Id = event.target.value;
    const newSizes = [...sizes];
    newSizes[index] = { ...newSizes[index], shutoff2: shutoff2Id || "" };
    setSizes(newSizes);
  };

  const handleThrottleChange = (event, index) => {
    const throttleId = event.target.value;
    const newSizes = [...sizes];
    newSizes[index] = { ...newSizes[index], throttle: throttleId || "" };
    setSizes(newSizes);
  };

  const handleCheckChange = (event, index) => {
    const checkId = event.target.value;
    const newSizes = [...sizes];
    newSizes[index] = { ...newSizes[index], check: checkId || "" };
    setSizes(newSizes);
  };

  const handleStrainerChange = (event, index) => {
    const strainerId = event.target.value;
    const newSizes = [...sizes];
    newSizes[index] = { ...newSizes[index], strainer: strainerId || "" };
    setSizes(newSizes);
  };

  const handleSystemChange = (event, index) => {
    const newSystems = [...systems];
    newSystems[index] = event.target.value;
    setSystems(newSystems);
  };
  const handleSizeChange = (event, index) => {
    const newEndSize = event.target.value;

    setSizes((prevSizes) => {
      const newSizes = [...prevSizes];

      // Update the current size
      const previousEndSize = prevSizes[index - 1]
        ? prevSizes[index - 1].end
        : "All";
      newSizes[index] = {
        ...newSizes[index],
        start: previousEndSize === "All" ? "All" : getNextSize(previousEndSize),
        end: newEndSize,
      };

      // Update all subsequent sizes to maintain proper sequence
      for (let i = index + 1; i < newSizes.length; i++) {
        const prevEndSize = newSizes[i - 1].end;
        newSizes[i] = {
          ...newSizes[i],
          start: prevEndSize === "All" ? "All" : getNextSize(prevEndSize),
        };
      }

      return newSizes;
    });
  };
  const handleAddSize = () => {
    const lastEndSize = sizes[sizes.length - 1].end;
    // Get the maximum size from allSizes array (last item should be the largest)
    const maxSize =
      allSizes.length > 0 ? allSizes[allSizes.length - 1].size : "All";

    const newSize = {
      start: getNextSize(lastEndSize),
      end: maxSize,
      isToggleOn: false,
      pipeOptions: [],
      fittingOptions: [],
      material: "",
      pipe: "",
      fitting: "",
      shutoff1: "",
      shutoff2: "",
      throttle: "",
      check: "",
      strainer: "",
    };
    setSizes([...sizes, newSize]);
    setActiveButtonIndex(sizes.length);
  };
  const handleDelSize = (indexToDelete) => {
    if (sizes.length > 1) {
      const sizeToDelete = sizes[indexToDelete];

      // If the size has an ID (meaning it exists in the database), mark it for deletion
      if (sizeToDelete.id) {
        setDeletedSizes((prevDeletedSizes) => [
          ...prevDeletedSizes,
          sizeToDelete.id,
        ]);
      }

      const newSizes = sizes.filter((_, index) => index !== indexToDelete);
      setSizes(newSizes);

      // Adjust active button index if needed
      if (indexToDelete === activeButtonIndex) {
        setActiveButtonIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : 0
        );
      } else if (indexToDelete < activeButtonIndex) {
        setActiveButtonIndex((prevIndex) => prevIndex - 1);
      }
    }
  };

  const handleButtonClick = (index) => {
    setActiveButtonIndex(index);
  };
  const getFilteredSizes = (index) => {
    const previousEndSize = sizes[index - 1] ? sizes[index - 1].end : "All";
    if (previousEndSize === "All") {
      return allSizes.map((size) => size.size); // Return size strings
    }
    const previousEndIndex = allSizes.findIndex(
      (size) => size.size === previousEndSize
    );
    return allSizes.slice(previousEndIndex).map((size) => size.size);
  };
  const getNextSize = (currentSize) => {
    const currentIndex = allSizes.findIndex(
      (size) => size.size === currentSize
    );
    return allSizes[currentIndex + 1]?.size || "All";
  };
  const handleToggle = (index) => {
    const newSizes = [...sizes];
    newSizes[index].isToggleOn = !newSizes[index].isToggleOn;
    setSizes(newSizes);
  };

  const handleAddSystem = () => {
    setSystems([...systems, ""]);
    setSystemInputTypes([...systemInputTypes, "dropdown"]);
    setNewSystemNames([...newSystemNames, ""]);
  };

  const handleDelSystem = (index) => {
    setSystems(systems.filter((_, i) => i !== index));
    setSystemInputTypes(systemInputTypes.filter((_, i) => i !== index));
    setNewSystemNames(newSystemNames.filter((_, i) => i !== index));
  };

  // New handlers for system input functionality
  const handleSystemInputTypeChange = (index, type) => {
    const newInputTypes = [...systemInputTypes];
    newInputTypes[index] = type;
    setSystemInputTypes(newInputTypes);

    // Clear the corresponding values when switching types
    if (type === "dropdown") {
      const newNewSystemNames = [...newSystemNames];
      newNewSystemNames[index] = "";
      setNewSystemNames(newNewSystemNames);
    } else {
      const newSystems = [...systems];
      newSystems[index] = "";
      setSystems(newSystems);
    }
  };

  const handleNewSystemNameChange = (index, value) => {
    const newNewSystemNames = [...newSystemNames];
    newNewSystemNames[index] = value;
    setNewSystemNames(newNewSystemNames);
  };

  const handleNewSpec = async () => {
    try {
      await handleSave(false, null);
      // Call the callback to handle showing the SpecificationModal again
      if (onNewSpecification) {
        onNewSpecification();
      }
    } catch (error) {
      console.error(
        "Error saving specification before creating new one:",
        error
      );
    }
  };
  // Save logic here...
  const handleSave = async (shouldClose = true, currentSizes = null) => {
    console.log("Starting save process...", {
      systems,
      systemInputTypes,
      newSystemNames,
    });

    // Use provided sizes or fall back to state
    const sizesToSave = currentSizes || sizes;
    const specificationId = editData && editData.id ? editData.id : uuidv4(); // Use existing ID if available, otherwise generate new
    setActiveButtonIndex(0);

    // First, create any new systems and collect all system IDs
    const finalSystemIds = [];

    for (let i = 0; i < systems.length; i++) {
      if (systemInputTypes[i] === "new" && newSystemNames[i].trim()) {
        // Create new system
        console.log(`Creating new system: ${newSystemNames[i].trim()}`);
        try {
          const newSystemResponse = await apiClient.post("/systems", {
            id: uuidv4(),
            name: newSystemNames[i].trim(),
          });

          if (!newSystemResponse.success) {
            throw new Error(
              newSystemResponse.error || "Failed to create new system"
            );
          }

          const newSystem = newSystemResponse.data;
          console.log("Successfully created system:", newSystem);
          finalSystemIds.push(newSystem.id);

          // Update allSystems state to include the new system
          setAllSystems((prev) => [...prev, newSystem]);

          // Update the form state to reflect the newly created system
          const newSystems = [...systems];
          const newInputTypes = [...systemInputTypes];
          const newSystemNamesArray = [...newSystemNames];

          newSystems[i] = newSystem.id;
          newInputTypes[i] = "dropdown";
          newSystemNamesArray[i] = "";

          setSystems(newSystems);
          setSystemInputTypes(newInputTypes);
          setNewSystemNames(newSystemNamesArray);
        } catch (error) {
          console.error("Error creating new system:", error);
          alert(`Failed to create system: ${newSystemNames[i]}`);
          return;
        }
      } else if (systemInputTypes[i] === "dropdown" && systems[i]) {
        // Use existing system ID
        finalSystemIds.push(systems[i]);
      }
    }

    console.log("Final system IDs:", finalSystemIds);

    const newFormData = {
      id: specificationId,
      name: specificationName,
      system_ids: finalSystemIds.filter((system) => system !== ""), // Use the final system IDs
      pvfsubmittal_id: pvfSubmittalId,
      sizes: sizesToSave.map((size) => ({
        spec_id: specificationId,
        max_size_id: allSizes.find((s) => s.size === size.end)?.id || null,
        min_size_id: allSizes.find((s) => s.size === size.start)?.id || null,
        material_id: size.material || null,
        pipe_id: size.pipe || null,
        fitting_id: size.fitting || null,
        notes: size.notes || "",
        valves: !size.isToggleOn,
        shutoff1_id: size.shutoff1 || null,
        shutoff2_id: size.shutoff2 || null,
        throttle_id: size.throttle || null,
        check_id: size.check || null,
        strainer_id: size.strainer || null,
      })),
    };

    try {
      // If editData.id exists, update (PUT) the existing specification, otherwise create (POST) a new one
      const specificationResponse =
        editData && editData.id
          ? await apiClient.put(`/specifications/${specificationId}`, {
              id: specificationId,
              name: specificationName,
              system_ids: finalSystemIds.filter((system) => system !== ""), // Use the final system IDs
              pvfsubmittal_id: pvfSubmittalId,
            })
          : await apiClient.post("/specifications", {
              id: specificationId,
              name: specificationName,
              system_ids: finalSystemIds.filter((system) => system !== ""), // Use the final system IDs
              pvfsubmittal_id: pvfSubmittalId,
            });

      // Check if the specification operation was successful
      if (!specificationResponse.success) {
        throw new Error(
          `Failed to ${
            editData && editData.id ? "update" : "save"
          } specification: ${specificationResponse.error || "Unknown error"}`
        );
      }

      // Loop through sizes and handle each one based on whether it's a new size or an existing one
      for (const size of sizesToSave) {
        const sizeDefinitionId = size.id || uuidv4(); // Use existing size ID for updates, otherwise generate a new one
        const minSize = allSizes.find((s) => s.size === size.start);
        const maxSize = allSizes.find((s) => s.size === size.end);

        const selectedMaterial = materials.find((m) => m.id === size.material);
        const selectedPipe = size.pipeOptions.find((p) => p.id === size.pipe);
        const selectedFitting = size.fittingOptions.find(
          (f) => f.id === size.fitting
        );
        const selectedShutoff1 = valveProducts.shutoffValve.find(
          (v) => v.id === size.shutoff1
        );
        const selectedShutoff2 = valveProducts.shutoffValve.find(
          (v) => v.id === size.shutoff2
        );
        const selectedThrottle = valveProducts.throttleValve.find(
          (v) => v.id === size.throttle
        );
        const selectedCheck = valveProducts.checkValve.find(
          (v) => v.id === size.check
        );
        const selectedStrainer = valveProducts.strainer.find(
          (s) => s.id === size.strainer
        );

        const sizeResponse = size.id
          ? await apiClient.put(`/size_definitions/${sizeDefinitionId}`, {
              id: sizeDefinitionId,
              spec_id: specificationId,
              max_size_id: maxSize ? maxSize.id : null,
              min_size_id: minSize ? minSize.id : null,
              material_id: selectedMaterial ? selectedMaterial.id : null,
              pipe_id: selectedPipe ? selectedPipe.id : null,
              fitting_id: selectedFitting ? selectedFitting.id : null,
              notes: size.notes || "",
              valves: !size.isToggleOn,
              shutoff1_id: selectedShutoff1 ? selectedShutoff1.id : null,
              shutoff2_id: selectedShutoff2 ? selectedShutoff2.id : null,
              throttle_id: selectedThrottle ? selectedThrottle.id : null,
              check_id: selectedCheck ? selectedCheck.id : null,
              strainer_id: selectedStrainer ? selectedStrainer.id : null,
            })
          : await apiClient.post("/size_definitions", {
              id: sizeDefinitionId,
              spec_id: specificationId,
              max_size_id: maxSize ? maxSize.id : null,
              min_size_id: minSize ? minSize.id : null,
              material_id: selectedMaterial ? selectedMaterial.id : null,
              pipe_id: selectedPipe ? selectedPipe.id : null,
              fitting_id: selectedFitting ? selectedFitting.id : null,
              notes: size.notes || "",
              valves: !size.isToggleOn,
              shutoff1_id: selectedShutoff1 ? selectedShutoff1.id : null,
              shutoff2_id: selectedShutoff2 ? selectedShutoff2.id : null,
              throttle_id: selectedThrottle ? selectedThrottle.id : null,
              check_id: selectedCheck ? selectedCheck.id : null,
              strainer_id: selectedStrainer ? selectedStrainer.id : null,
            });

        // Check if the size definition operation was successful
        if (!sizeResponse.success) {
          throw new Error(
            `Failed to ${size.id ? "update" : "save"} size definition: ${
              sizeResponse.error || "Unknown error"
            }`
          );
        }
      }

      // Delete sizes marked for deletion
      const validDeletedSizes = deletedSizes.filter(
        (id) => id && id !== null && id !== undefined
      );

      for (const sizeId of validDeletedSizes) {
        try {
          const deleteResponse = await apiClient.delete(
            `/size_definitions/${sizeId}`
          );

          // Check if deletion failed based on the new standardized response structure
          if (!deleteResponse.success) {
            throw new Error(
              `API returned deletion failure: ${
                deleteResponse.error || "Unknown error"
              }`
            );
          }
        } catch (deleteError) {
          // If it's a 404 error, the item is already gone, which is what we want
          if (deleteError.message && deleteError.message.includes("404")) {
            continue;
          }

          throw new Error(
            `Failed to delete size definition with ID ${sizeId}: ${
              deleteError.message || deleteError
            }`
          );
        }
      }

      // Update submittalData in parent component
      onSubmit(newFormData);

      if (shouldClose) {
        // In continuous adding mode, reopen SpecificationModal instead of closing
        if (isContinuousAddingMode && onNewSpecification) {
          onNewSpecification();
        } else {
          onClose();
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      alert(`Failed to save data: ${error.message || error}`);
    } finally {
      // Clear the deleted sizes after save
      setDeletedSizes([]);
    }
  };

  const handleAddProduct = () => {
    setIsAddProductModalOpen(true);
  };

  const handleCloseAddProductModal = () => {
    setIsAddProductModalOpen(false);
  };

  if (!isOpen) {
    return null;
  }

  console.log("activeButtonIndex", activeButtonIndex);

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-7xl h-4/5 overflow-auto flex flex-col">
        <div className="flex flex-col gap-8 p-5 bg-white rounded-2xl shadow border border-gray-100">
          <div className="flex flex-col gap-2.5">
            <div className="flex justify-between items-center gap-2.5">
              <div className="flex flex-col flex-1 gap-2">
                <label className="text-gray-900 text-sm font-medium">
                  Specification
                </label>
                <input
                  type="text"
                  placeholder="Enter specification"
                  className="h-10 p-2.5 bg-white rounded-md shadow-inner border border-gray-300 w-2/3"
                  value={specificationName}
                  onChange={(e) => setSpecificationName(e.target.value)}
                />
              </div>
              <div className="flex items-end gap-4">
                <button
                  type="button"
                  className="w-50 px-3 py-2 text-sm font-medium text-green-600 border border-green-600 rounded-md hover:bg-green-50"
                  onClick={handleAddProduct}
                >
                  Add Product
                </button>
                <button
                  type="button"
                  className="w-50 bg-white text-gray-900 text-sm font-semibold px-4 py-2 rounded-md shadow-sm border border-gray-300"
                  onClick={handleNewSpec}
                >
                  New Specification
                </button>
                <button
                  type="button"
                  className="w-50 bg-indigo-600 text-white text-sm font-semibold px-4 py-2 rounded-md shadow-sm"
                  onClick={() => handleSave(true)}
                >
                  Save and Close
                </button>
                <button
                  type="button"
                  className="w-32 bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-md shadow-sm"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-4">
              <label className="text-gray-900 text-sm font-medium">
                Systems
              </label>
              <div className="flex flex-wrap gap-2.5">
                {systems.map((system, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border"
                  >
                    {/* Toggle buttons for dropdown vs new system */}
                    <div className="flex gap-1 bg-white rounded-md p-1">
                      <button
                        type="button"
                        className={`px-2 py-1 text-xs rounded ${
                          systemInputTypes[index] === "new"
                            ? "bg-indigo-600 text-white"
                            : "bg-indigo-600 text-white hover:bg-indigo-700"
                        }`}
                        onClick={() =>
                          handleSystemInputTypeChange(index, "new")
                        }
                      >
                        Add Custom System
                      </button>
                    </div>

                    {/* Conditional input based on type */}
                    {systemInputTypes[index] === "dropdown" ? (
                      <SearchableSelect
                        options={allSystems
                          .filter(
                            (sys) => !sys.is_user_added || sys.id === system
                          )
                          .map((sys) => ({
                            value: sys.id,
                            label: sys.name,
                            isDisabled: sys.is_user_added,
                          }))}
                        value={system || ""}
                        onChange={(selectedOption) => {
                          const event = {
                            target: {
                              value: selectedOption ? selectedOption.value : "",
                            },
                          };
                          handleSystemChange(event, index);
                        }}
                        placeholder="Select System"
                        isClearable={true}
                      />
                    ) : (
                      <input
                        type="text"
                        placeholder="Enter new system name"
                        className="h-10 p-2.5 bg-white rounded-md shadow-inner border border-gray-300"
                        value={newSystemNames[index] || ""}
                        onChange={(e) =>
                          handleNewSystemNameChange(index, e.target.value)
                        }
                      />
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="h-10 w-10 bg-gray-200 rounded-md shadow-inner flex justify-center items-center"
                  onClick={handleAddSystem}
                >
                  +
                </button>
                {systems.length > 1 && (
                  <button
                    type="button"
                    className="h-10 w-10 bg-gray-200 rounded-md shadow-inner flex justify-center items-center"
                    onClick={() => handleDelSystem(systems.length - 1)}
                  >
                    -
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Remaining form components here... */}
        <div className="max-w-full flex flex-col pt-5 px-8">
          <div className="flex items-center bg-white rounded-lg shadow-sm">
            {sizes.map((size, index) => (
              <div key={index} className="relative flex items-center">
                <button
                  type="button"
                  className={`py-4 px-6 text-sm font-medium ${
                    activeButtonIndex === index
                      ? "text-gray-900 border-b-2 border-indigo-600"
                      : "text-gray-500"
                  }`}
                  onClick={() => handleButtonClick(index)}
                >
                  {size.start === "All" && size.end === "All"
                    ? "All"
                    : `${size.start === "All" ? "Up to" : `${size.start} to`} ${
                        size.end
                      }`}
                </button>
                {sizes.length > 1 && (
                  <button
                    type="button"
                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs leading-none hover:bg-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelSize(index);
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="py-4 px-6 text-gray-500 text-sm font-medium"
              onClick={handleAddSize}
            >
              Add Size
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-10 pt-5 px-8">
          {sizes.map((size, index) => (
            <div
              key={index}
              className={`flex flex-col gap-8 ${
                activeButtonIndex === index ? "" : "hidden"
              }`}
            >
              <div className="flex gap-10">
                <div className="flex flex-col gap-8 w-1/2">
                  <div className="text-gray-900 text-lg font-semibold">
                    Pipe and Fittings for{" "}
                    {size.start === "All"
                      ? "All"
                      : `${size.start} to ${size.end}`}
                  </div>
                  <div className="flex flex-col gap-2.5 p-5 bg-white rounded-2xl shadow border border-gray-100">
                    <div className="flex flex-col gap-2">
                      <label className="text-gray-900 text-sm font-semibold">
                        Through Size
                      </label>
                      <SearchableSelect
                        options={getFilteredSizes(index).map((option) => ({
                          value: option,
                          label: option,
                        }))}
                        value={size.end || ""}
                        onChange={(selectedOption) => {
                          const event = {
                            target: {
                              value: selectedOption ? selectedOption.value : "",
                            },
                          };
                          handleSizeChange(event, index);
                        }}
                        placeholder="Select Size"
                        isClearable={false}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-gray-900 text-sm font-semibold">
                        Material
                      </label>
                      <SearchableSelect
                        options={materials.map((material) => ({
                          value: material.id,
                          label: material.name,
                        }))}
                        value={size.material || ""}
                        onChange={(selectedOption) => {
                          const event = {
                            target: {
                              value: selectedOption ? selectedOption.value : "",
                            },
                          };
                          handleMaterialChange(event, index);
                        }}
                        placeholder="Select Material"
                        isClearable={true}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-gray-900 text-sm font-medium">
                        Pipe
                      </label>
                      <SearchableSelect
                        options={size.pipeOptions.map((pipe) => ({
                          value: pipe.id,
                          label: pipe.name,
                        }))}
                        value={size.pipe || ""}
                        onChange={(selectedOption) => {
                          const event = {
                            target: {
                              value: selectedOption ? selectedOption.value : "",
                            },
                          };
                          handlePipeChange(event, index);
                        }}
                        placeholder="Select Pipe"
                        isClearable={true}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-gray-900 text-sm font-medium">
                        Fittings
                      </label>
                      <SearchableSelect
                        options={size.fittingOptions.map((fitting) => ({
                          value: fitting.id,
                          label: fitting.name,
                        }))}
                        value={size.fitting || ""}
                        onChange={(selectedOption) => {
                          const event = {
                            target: {
                              value: selectedOption ? selectedOption.value : "",
                            },
                          };
                          handleFittingChange(event, index);
                        }}
                        placeholder="Select Fitting"
                        isClearable={true}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-gray-900 text-sm font-medium">
                        Notes
                      </label>
                      <textarea
                        className="h-24 p-2.5 bg-white rounded-md shadow-inner border border-gray-300"
                        value={size.notes || ""}
                        onChange={(e) => {
                          const newSizes = [...sizes];
                          newSizes[index].notes = e.target.value;
                          setSizes(newSizes);
                        }}
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-8 w-1/2">
                  <div className="flex items-center justify-between">
                    <div className="text-gray-900 text-lg font-semibold">
                      Valves for{" "}
                      {size.start === "All"
                        ? "All"
                        : `${size.start} to ${size.end}`}
                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-6 rounded-full relative flex-shrink-0 cursor-pointer ${
                          size.isToggleOn ? "bg-indigo-600" : "bg-gray-300"
                        }`}
                        onClick={() => handleToggle(index)}
                      >
                        <div
                          className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${
                            size.isToggleOn ? "transform translate-x-4" : ""
                          }`}
                        ></div>
                      </div>
                      <span className="text-gray-900 text-sm font-medium">
                        Not Required
                      </span>
                    </div>
                  </div>
                  {!size.isToggleOn && (
                    <div className="flex flex-col gap-2.5 p-5 bg-white rounded-2xl shadow border border-gray-100">
                      <div className="flex flex-col gap-2">
                        <label className="text-gray-900 text-sm font-medium">
                          Shutoff Valve 1
                        </label>
                        <SearchableSelect
                          options={valveProducts.shutoffValve.map((valve) => ({
                            value: valve.id,
                            label: valve.name,
                          }))}
                          value={size.shutoff1 || ""}
                          onChange={(selectedOption) => {
                            const event = {
                              target: {
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            };
                            handleShutoff1Change(event, index);
                          }}
                          placeholder="Select Shutoff Valve 1"
                          isClearable={true}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-gray-900 text-sm font-medium">
                          Shutoff Valve 2
                        </label>
                        <SearchableSelect
                          options={valveProducts.shutoffValve.map((valve) => ({
                            value: valve.id,
                            label: valve.name,
                          }))}
                          value={size.shutoff2 || ""}
                          onChange={(selectedOption) => {
                            const event = {
                              target: {
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            };
                            handleShutoff2Change(event, index);
                          }}
                          placeholder="Select Shutoff Valve 2"
                          isClearable={true}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-gray-900 text-sm font-medium">
                          Throttle Valve
                        </label>
                        <SearchableSelect
                          options={valveProducts.throttleValve.map((valve) => ({
                            value: valve.id,
                            label: valve.name,
                          }))}
                          value={size.throttle || ""}
                          onChange={(selectedOption) => {
                            const event = {
                              target: {
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            };
                            handleThrottleChange(event, index);
                          }}
                          placeholder="Select Throttle Valve"
                          isClearable={true}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-gray-900 text-sm font-medium">
                          Check Valve
                        </label>
                        <SearchableSelect
                          options={valveProducts.checkValve.map((valve) => ({
                            value: valve.id,
                            label: valve.name,
                          }))}
                          value={size.check || ""}
                          onChange={(selectedOption) => {
                            const event = {
                              target: {
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            };
                            handleCheckChange(event, index);
                          }}
                          placeholder="Select Check Valve"
                          isClearable={true}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-gray-900 text-sm font-medium">
                          Strainer
                        </label>
                        <SearchableSelect
                          options={valveProducts.strainer.map((strainer) => ({
                            value: strainer.id,
                            label: strainer.name,
                          }))}
                          value={size.strainer || ""}
                          onChange={(selectedOption) => {
                            const event = {
                              target: {
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            };
                            handleStrainerChange(event, index);
                          }}
                          placeholder="Select Strainer"
                          isClearable={true}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AddPipeFittingModal
        isOpen={isAddProductModalOpen}
        onClose={handleCloseAddProductModal}
      />
    </div>
  );
}

export default SpecificationFormModal;
